import { getIsFunctionContainer } from './expressionNode';
import type { ExpressionNode } from './expressionNode';
import { RuleEditorDropdownMode } from '../NodeEditorDrawer/types';

export enum ExpressionInsertionType {
  /** add the trailing min/max back, which draws the comma inviting another argument */
  REOPEN_FUNCTION = 'REOPEN_FUNCTION',
  /** wrap the operand with its new operator, so it applies to that argument only */
  WRAP_OPERAND = 'WRAP_OPERAND',
  SWITCH_FUNCTION = 'SWITCH_FUNCTION',
  /** replace the whole group, removing the data routes inside it */
  REPLACE_GROUP = 'REPLACE_GROUP',
  /** create an empty min/max group, shown as Min ( ) */
  OPEN_FUNCTION = 'OPEN_FUNCTION',
  CREATE_DATA_ROUTE = 'CREATE_DATA_ROUTE',
  /** create an empty pair of parentheses, since there is nothing after them to enclose */
  CREATE_GROUP = 'CREATE_GROUP',
  /** open a parenthesis and wait for the ) that closes it around what follows */
  PEND_GROUP = 'PEND_GROUP',
  CLOSE_GROUP = 'CLOSE_GROUP',
  CREATE_ITEM = 'CREATE_ITEM',
  UPDATE_FUNCTION = 'UPDATE_FUNCTION',
  UPDATE_ITEM = 'UPDATE_ITEM',
  NONE = 'NONE',
}

export enum ExpressionOptionCategory {
  ACTION = 'action',
  FUNCTION = 'function',
  FUNCTION_SEPARATOR = 'functionSeparator',
  GROUPING = 'grouping',
  OPERATOR = 'operator',
}

/**
 * What picking an option does to the expression, given where the dropdown stands. Order matters: the
 * first matching branch wins.
 */
export function resolveExpressionInsertion({
  node,
  mode,
  category,
  value,
  isFunctionOpening,
  hasItem,
  hasItemsToGroupAfter,
}: {
  node?: ExpressionNode;
  mode: RuleEditorDropdownMode;
  category?: string;
  value?: string;
  isFunctionOpening?: boolean;
  /** the dropdown is on an item that already exists, not on an empty caret position */
  hasItem?: boolean;
  hasItemsToGroupAfter?: boolean;
}): { type: ExpressionInsertionType } {
  const isExpressionMode = mode === RuleEditorDropdownMode.EXPRESSION
    || mode === RuleEditorDropdownMode.CARET_EXPRESSION;

  if (category === ExpressionOptionCategory.FUNCTION_SEPARATOR) {
    return { type: ExpressionInsertionType.REOPEN_FUNCTION };
  }

  if (isExpressionMode && !hasItem
    && category === ExpressionOptionCategory.OPERATOR
    && getIsFunctionContainer(node?.container)) {
    return { type: ExpressionInsertionType.WRAP_OPERAND };
  }

  if (isFunctionOpening) {
    return {
      type: category === ExpressionOptionCategory.FUNCTION
        ? ExpressionInsertionType.SWITCH_FUNCTION
        : ExpressionInsertionType.REPLACE_GROUP,
    };
  }

  if (!isExpressionMode) return { type: ExpressionInsertionType.NONE };

  if (hasItem) {
    return {
      type: category === ExpressionOptionCategory.FUNCTION
        ? ExpressionInsertionType.UPDATE_FUNCTION
        : ExpressionInsertionType.UPDATE_ITEM,
    };
  }

  if (category === ExpressionOptionCategory.FUNCTION) {
    return { type: ExpressionInsertionType.OPEN_FUNCTION };
  }

  if (category === ExpressionOptionCategory.ACTION) {
    return { type: ExpressionInsertionType.CREATE_DATA_ROUTE };
  }

  if (category === ExpressionOptionCategory.GROUPING) {
    if (value === ')') return { type: ExpressionInsertionType.CLOSE_GROUP };

    return {
      type: hasItemsToGroupAfter
        ? ExpressionInsertionType.PEND_GROUP
        : ExpressionInsertionType.CREATE_GROUP,
    };
  }

  return { type: ExpressionInsertionType.CREATE_ITEM };
}
